// My Pictures — the nostalgia pool, the meme arsenal and every piece of art we own, as a Windows XP
// Explorer window in Thumbnails view. Data comes from /gallery/manifest.json (tools/pictures.ts); nothing
// is hardcoded. Kris 2026-09-08: "add the memes and other art that we have… in a separate place on the
// website". Deliberately separate from My Videos: its own folder, its own manifest, its own window.
// Restyled 2026-09-12 to real Luna (lib/xp): blue task pane, white thumbnails with a shadow, blue selection.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import { XP, xpCss, sunken } from 'lib/xp';
import { File, Help, viewMenu } from './dropDownData';
import go from 'assets/windowsIcons/290.png';
import search from 'assets/windowsIcons/299(32x32).png';
import back from 'assets/windowsIcons/back.png';
import forward from 'assets/windowsIcons/forward.png';
import up from 'assets/windowsIcons/up.png';
import folderOpen from 'assets/windowsIcons/337(32x32).png';
import menu from 'assets/windowsIcons/358(32x32).png';
import dropdown from 'assets/windowsIcons/dropdown.png';
import pullup from 'assets/windowsIcons/pullup.png';
import windows from 'assets/windowsIcons/windows.png';
import computer from 'assets/windowsIcons/676(16x16).png';
import picturesIcon from 'assets/windowsIcons/307(32x32).png';
import videosIcon from 'assets/windowsIcons/myvideos16.png';
import paintIcon from 'assets/windowsIcons/680(16x16).png';
import ieIcon from 'assets/windowsIcons/ie-paper.png';

const MANIFEST_URL = '/gallery/manifest.json';
const PATH = 'C:\\Documents and Settings\\fren\\My Documents\\My Pictures';
const SLIDE_MS = 4500;

const mb = bytes => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
function openApp(name) {
  window.dispatchEvent(new CustomEvent('ic:open', { detail: name }));
}

function Pictures({ onClose, isFocus, group: openWith }) {
  const [data, setData] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);
  const [group, setGroup] = useState(openWith || 'all');
  const [index, setIndex] = useState(0);
  const [view, setView] = useState(() => (window.innerWidth < 800 ? 'thumbs' : 'thumbs'));
  const [preview, setPreview] = useState(false);
  const [slideshow, setSlideshow] = useState(false);
  const [about, setAbout] = useState(false);
  const [note, setNote] = useState('');
  const [reload, setReload] = useState(0);
  const gridRef = useRef(null);
  const noteTimer = useRef(null);
  const checkedOpenWith = useRef(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(false);
    fetch(`${MANIFEST_URL}?t=${reload}`)
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(m => alive && setData(m))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [reload]);

  const groups = data ? data.groups : [];
  const items = useMemo(() => {
    if (!data) return [];
    return group === 'all' ? data.items : data.items.filter(i => i.group === group);
  }, [data, group]);
  const count = items.length;
  const current = items[Math.min(index, Math.max(0, count - 1))];
  const totalBytes = useMemo(() => items.reduce((s, i) => s + (i.bytes || 0), 0), [items]);

  // A caller can preselect a folder: ic:open → { app: 'My Pictures', props: { group: 'core' } }.
  // If that folder isn't in the manifest the window would open on an empty grid, so fall back once
  // — once only, so it never overrides a folder the user picks later.
  useEffect(() => {
    if (!data || checkedOpenWith.current) return;
    checkedOpenWith.current = true;
    if (group !== 'all' && !data.groups.some(g => g.id === group)) setGroup('all');
  }, [data, group]);

  const select = useCallback(
    i => {
      if (!count) return;
      setIndex(((i % count) + count) % count);
    },
    [count],
  );
  const next = useCallback(() => select(index + 1), [select, index]);
  const prev = useCallback(() => select(index - 1), [select, index]);

  function flash(text) {
    setNote(text);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(''), 4000);
  }
  useEffect(() => () => clearTimeout(noteTimer.current), []);

  // keep the selected thumbnail in view
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const el = grid.querySelector('[data-selected="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [index, view, group]);

  // slideshow auto-advance
  useEffect(() => {
    if (!slideshow || count < 2) return undefined;
    const t = setInterval(() => setIndex(i => (i + 1) % count), SLIDE_MS);
    return () => clearInterval(t);
  }, [slideshow, count]);

  // arrow keys surf while this window is focused; Esc leaves preview / slideshow
  useEffect(() => {
    if (!isFocus) return undefined;
    function onKey(e) {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); prev(); }
      else if (e.key === 'Enter') { e.preventDefault(); setPreview(true); }
      else if (e.key === 'Escape') { setSlideshow(false); setPreview(false); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function setWallpaper() {
    if (!current) return;
    window.dispatchEvent(new CustomEvent('ic:wallpaper', { detail: { src: current.src, name: current.name } }));
    flash(`desktop background set to "${current.name}" — right-click the desktop… no wait, use Wallpapers → Restore.`);
  }
  function savePicture() {
    if (!current) return;
    const a = document.createElement('a');
    a.href = current.src;
    a.download = current.file || `${current.name.replace(/[^a-z0-9]+/gi, '_')}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  function startSlideshow() {
    setSlideshow(true);
    setPreview(true);
  }
  function onClickOptionItem(item) {
    switch (item) {
      case 'Close':
        onClose();
        break;
      case 'View as a slide show':
        startSlideshow();
        break;
      case 'Set as desktop background':
        setWallpaper();
        break;
      case 'Save this picture':
        savePicture();
        break;
      case 'Thumbnails':
        setView('thumbs');
        break;
      case 'Filmstrip':
        setView('filmstrip');
        break;
      case 'Details':
        setView('details');
        break;
      case 'Refresh':
        setReload(r => r + 1);
        break;
      case 'About My Pictures':
        setAbout(a => !a);
        break;
      default:
    }
  }

  const groupLabel = group === 'all' ? 'All pictures' : (groups.find(g => g.id === group) || {}).label || group;

  const thumb = (it, i) => (
    <div
      key={it.id}
      className={`pic__tile${i === index ? ' pic__tile--on' : ''}`}
      data-selected={i === index ? 'true' : 'false'}
      data-id={it.id}
      onClick={() => select(i)}
      onDoubleClick={() => { select(i); setPreview(true); }}
      title={`${it.name}${it.caption ? ` — ${it.caption}` : ''}`}
    >
      <div className="pic__tile__frame">
        <img src={it.thumb} alt={it.name} loading="lazy" />
      </div>
      <div className="pic__tile__name">{it.name}</div>
    </div>
  );

  return (
    <Div>
      <section className="pic__toolbar">
        <div className="pic__options">
          <WindowDropDowns items={{ File, View: viewMenu(view), Help }} onClickItem={onClickOptionItem} height={21} />
        </div>
        <img className="pic__windows-logo" src={windows} alt="windows" />
      </section>
      <section className="pic__function_bar">
        <div className={`pic__function_bar__button${index === 0 ? '--disable' : ''}`} onClick={prev}>
          <img className="pic__function_bar__icon" src={back} alt="" />
          <span className="pic__function_bar__text">Back</span>
          <div className="pic__function_bar__arrow" />
        </div>
        <div className={`pic__function_bar__button${index >= count - 1 ? '--disable' : ''}`} onClick={next}>
          <img className="pic__function_bar__icon" src={forward} alt="" />
          <div className="pic__function_bar__arrow" />
        </div>
        <div className="pic__function_bar__button" onClick={() => openApp('My Computer')}>
          <img className="pic__function_bar__icon--normalize" src={up} alt="" />
        </div>
        <div className="pic__function_bar__separate" />
        <div className="pic__function_bar__button" onClick={() => select(Math.floor(Math.random() * Math.max(1, count)))}>
          <img className="pic__function_bar__icon--normalize" src={search} alt="" />
          <span className="pic__function_bar__text">Random</span>
        </div>
        <div className="pic__function_bar__button" onClick={() => setGroup('all')}>
          <img className="pic__function_bar__icon--normalize" src={folderOpen} alt="" />
          <span className="pic__function_bar__text">Folders</span>
        </div>
        <div className="pic__function_bar__separate" />
        <div className="pic__function_bar__button" onClick={() => setView(v => (v === 'thumbs' ? 'filmstrip' : v === 'filmstrip' ? 'details' : 'thumbs'))}>
          <img className="pic__function_bar__icon--normalize" src={menu} alt="" />
          <div className="pic__function_bar__arrow" />
        </div>
      </section>
      <section className="pic__address_bar">
        <div className="pic__address_bar__title">Address</div>
        <div className="pic__address_bar__content">
          <img src={picturesIcon} alt="" className="pic__address_bar__content__img" />
          <div className="pic__address_bar__content__text">
            {PATH}
            {group === 'all' ? '' : `\\${groupLabel}`}
          </div>
          <img src={dropdown} alt="" className="pic__address_bar__content__img" />
        </div>
        <div className="pic__address_bar__go">
          <img className="pic__address_bar__go__img" src={go} alt="" />
          <span>Go</span>
        </div>
      </section>

      <div className="pic__body">
        <aside className="pic__side">
          <div className="pic__panel pic__panel--hi">
            <div className="pic__panel__head"><span>Picture Tasks</span><img src={pullup} alt="" /></div>
            <div className="pic__panel__body">
              <div className="pic__link" onClick={startSlideshow}>▶ View as a slide show</div>
              <div className="pic__link" onClick={setWallpaper}>🖼 Set as desktop background</div>
              <div className="pic__link" onClick={savePicture}>💾 Save this picture</div>
              <div className="pic__link" onClick={() => window.dispatchEvent(new CustomEvent('ic:wallpaper', { detail: null }))}>↺ Restore the original wallpaper</div>
            </div>
          </div>
          <div className="pic__panel">
            <div className="pic__panel__head"><span>Folders</span><img src={pullup} alt="" /></div>
            <div className="pic__panel__body">
              <div className={`pic__link${group === 'all' ? ' pic__link--on' : ''}`} onClick={() => { setGroup('all'); setIndex(0); }}>
                <img src={picturesIcon} alt="" />All pictures <em>{data ? data.count : ''}</em>
              </div>
              {groups.map(g => (
                <div key={g.id} className={`pic__link${group === g.id ? ' pic__link--on' : ''}`} onClick={() => { setGroup(g.id); setIndex(0); }} title={g.blurb}>
                  <img src={folderOpen} alt="" />{g.label} <em>{g.count}</em>
                </div>
              ))}
            </div>
          </div>
          <div className="pic__panel">
            <div className="pic__panel__head"><span>Other Places</span><img src={pullup} alt="" /></div>
            <div className="pic__panel__body">
              <div className="pic__link" onClick={() => openApp('My Videos')}><img src={videosIcon} alt="" />My Videos</div>
              <div className="pic__link" onClick={() => openApp('Paint')}><img src={paintIcon} alt="" />Paint</div>
              <div className="pic__link" onClick={() => openApp('Internet Explorer')}><img src={ieIcon} alt="" />The Internet</div>
              <div className="pic__link" onClick={() => openApp('My Computer')}><img src={computer} alt="" />My Computer</div>
            </div>
          </div>
          <div className="pic__panel">
            <div className="pic__panel__head"><span>Details</span><img src={pullup} alt="" /></div>
            <div className="pic__panel__body pic__details">
              {current ? (
                <>
                  <b>{current.name}</b>
                  <div>{(groups.find(g => g.id === current.group) || {}).label || current.group}</div>
                  <div>{current.width} × {current.height} · {mb(current.bytes)}</div>
                  {current.title ? <div className="pic__details__q">“{current.title}”</div> : null}
                  {current.caption ? <div className="pic__details__c">{current.caption}</div> : null}
                  {current.link ? (
                    <a className="pic__a pic__details__src" href={current.link} target="_blank" rel="noopener noreferrer">↗ where it came from</a>
                  ) : null}
                </>
              ) : (
                <>
                  <b>My Pictures</b>
                  <div>File Folder</div>
                  <div className="pic__details__c">every meme, poster, wallpaper and logo the internet has made. take them.</div>
                </>
              )}
            </div>
          </div>
        </aside>

        <div className="pic__content" ref={gridRef}>
          {data === null && !failed && <div className="pic__empty">opening My Pictures…</div>}
          {failed && (
            <div className="pic__empty">
              <b>The pictures are not available.</b>
              <p>the gallery manifest didn’t load. <span className="pic__a" onClick={() => setReload(r => r + 1)}>try again</span></p>
            </div>
          )}
          {data && count === 0 && <div className="pic__empty">This folder is empty.</div>}
          {data && count > 0 && view === 'thumbs' && <div className="pic__grid">{items.map(thumb)}</div>}
          {data && count > 0 && view === 'filmstrip' && (
            <div className="pic__filmstrip">
              <div className="pic__stage" onDoubleClick={() => setPreview(true)}>
                <img src={current.src} alt={current.name} />
                <div className="pic__stage__bar">
                  <button type="button" className="xp__btn" onClick={prev} title="Previous">◀</button>
                  <button type="button" className="xp__btn" onClick={() => setPreview(true)} title="Full size">⛶</button>
                  <button type="button" className="xp__btn" onClick={next} title="Next">▶</button>
                  <span>{index + 1} / {count}</span>
                </div>
              </div>
              <div className="pic__strip">{items.map(thumb)}</div>
            </div>
          )}
          {data && count > 0 && view === 'details' && (
            <table className="pic__table">
              <thead><tr><th>Name</th><th>Folder</th><th>Size</th><th>Dimensions</th><th>Caption</th></tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id} className={i === index ? 'on' : ''} onClick={() => select(i)} onDoubleClick={() => { select(i); setPreview(true); }}>
                    <td className="c-name"><img src={it.thumb} alt="" />{it.name}</td>
                    <td>{(groups.find(g => g.id === it.group) || {}).label || it.group}</td>
                    <td>{mb(it.bytes)}</td>
                    <td>{it.width} × {it.height}</td>
                    <td className="c-dim">{it.caption}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {preview && current && (
          <div className="pic__preview" onClick={() => { setPreview(false); setSlideshow(false); }}>
            <div className="pic__preview__img" onClick={e => e.stopPropagation()}>
              <img src={current.src} alt={current.name} />
            </div>
            <div className="pic__preview__bar" onClick={e => e.stopPropagation()}>
              <button type="button" className="xp__btn" onClick={prev}>◀ Prev</button>
              <button type="button" className="xp__btn" onClick={() => setSlideshow(s => !s)}>{slideshow ? '❚❚ Pause' : '▶ Slide show'}</button>
              <button type="button" className="xp__btn" onClick={next}>Next ▶</button>
              <button type="button" className="xp__btn" onClick={setWallpaper}>Set as background</button>
              <button type="button" className="xp__btn" onClick={savePicture}>Save</button>
              <button type="button" className="xp__btn" onClick={() => { setPreview(false); setSlideshow(false); }}>Close ✕</button>
            </div>
            <div className="pic__preview__meta" onClick={e => e.stopPropagation()}>
              <b>{current.name}</b>
              {current.title ? <span className="pic__preview__t">“{current.title}”</span> : null}
              {current.caption ? <span className="pic__preview__c">{current.caption}</span> : null}
              {current.link ? (
                <a className="pic__preview__src" href={current.link} target="_blank" rel="noopener noreferrer">↗ where it came from</a>
              ) : null}
              <span className="pic__preview__n">{index + 1} / {count} · {current.width} × {current.height}</span>
            </div>
          </div>
        )}

        {about && (
          <div className="pic__about" onClick={() => setAbout(false)}>
            <div className="pic__about__box" onClick={e => e.stopPropagation()}>
              <b>My Pictures</b>
              <p>{data ? data.count : 0} pictures: the internet core pool, the Y2K meme arsenal, the newest cards, the scenes without their lettering, and the logo set.</p>
              <p>All of it is ours and all of it is yours — save any of it, post any of it. Double-click for full size, or set one as the desktop background.</p>
              <p>the internet keeps its pictures. (real)</p>
              <span className="pic__about__ok" onClick={() => setAbout(false)}>OK</span>
            </div>
          </div>
        )}
      </div>

      <footer className="pic__status">
        <div className="pic__status__cell pic__status__cell--grow">
          {note || (current ? `${current.name} · ${current.width} × ${current.height}` : `${count} objects`)}
        </div>
        <div className="pic__status__cell">{count} objects</div>
        <div className="pic__status__cell">{mb(totalBytes)}</div>
        <div className="pic__status__cell"><img src={computer} alt="" />My Computer</div>
      </footer>
    </Div>
  );
}

// Real Luna Explorer: warm-beige toolbars, the blue task pane with white panels (the "Picture Tasks"
// panel gets the dark-blue header, as in XP), white content, thumbnails on white cards with a shadow,
// #316ac5 selection. The preview is a beige viewer panel, not a black overlay.
const Div = styled.div`
  ${xpCss};
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  .pic__toolbar { position: relative; display: flex; align-items: center; height: 24px; border-bottom: 1px solid rgba(255,255,255,.7); flex-shrink: 0; background: linear-gradient(to right,#edede5 0%,#ede8cd 100%); }
  .pic__options { height: 23px; border-bottom: 1px solid rgba(0,0,0,.15); border-right: 1px solid rgba(0,0,0,.15); padding-left: 2px; flex: 1; }
  .pic__windows-logo { height: 100%; border-left: 1px solid white; border-bottom: 1px solid rgba(0,0,0,.1); }
  .pic__function_bar { height: 36px; display: flex; align-items: center; padding: 1px 3px 0; border-bottom: 1px solid rgba(0,0,0,.1); flex-shrink: 0; background: linear-gradient(to right,#edede5 0%,#ede8cd 100%); }
  .pic__function_bar__button { display: flex; height: 100%; align-items: center; border: 1px solid transparent; border-radius: 3px; cursor: default; padding: 0 2px;
    &:hover { border: 1px solid rgba(0,0,0,.1); box-shadow: inset 0 -1px 1px rgba(0,0,0,.1); } }
  .pic__function_bar__button--disable { filter: grayscale(1); opacity: .55; display: flex; height: 100%; align-items: center; border: 1px solid transparent; padding: 0 2px; }
  .pic__function_bar__text { margin-right: 4px; }
  .pic__function_bar__icon { height: 30px; width: 30px; &--normalize { height: 22px; width: 22px; margin: 0 4px 0 1px; } }
  .pic__function_bar__separate { height: 90%; width: 1px; background: rgba(0,0,0,.2); margin: 0 2px; }
  .pic__function_bar__arrow { height: 100%; display: flex; align-items: center; margin: 0 4px;
    &:before { content: ''; display: block; border-width: 3px 3px 0; border-color: #000 transparent; border-style: solid; } }
  .pic__address_bar { border-top: 1px solid rgba(255,255,255,.7); height: 22px; display: flex; align-items: center; padding: 0 2px 2px; box-shadow: inset 0 -2px 3px -1px #2d2d2d; flex-shrink: 0; background: linear-gradient(to right,#edede5 0%,#ede8cd 100%); }
  .pic__address_bar__title { color: rgba(0,0,0,.5); padding: 5px; }
  .pic__address_bar__content { border: 1px solid #7f9db9; height: 100%; display: flex; flex: 1; align-items: center; background: #fff; position: relative;
    &__img { width: 14px; height: 14px; }
    &__img:last-child { width: 15px; height: 15px; right: 1px; position: absolute; }
    &__text { position: absolute; white-space: nowrap; left: 16px; right: 17px; overflow: hidden; } }
  .pic__address_bar__go { display: flex; align-items: center; padding: 0 18px 0 5px; height: 100%; &__img { height: 95%; margin-right: 3px; } }

  .pic__body { flex: 1; display: flex; min-height: 0; position: relative; }
  .pic__side { width: 190px; flex-shrink: 0; background: linear-gradient(to bottom, #7ba2e7 0%, #6375d6 100%); padding: 10px; overflow-y: auto; }
  .pic__panel { background: #fff; border: 1px solid #fff; border-radius: 3px 3px 0 0; margin-bottom: 10px; box-shadow: 0 1px 1px rgba(0,0,0,.2); }
  .pic__panel__head { display: flex; justify-content: space-between; align-items: center; height: 23px; padding: 0 8px 0 10px; font-weight: 700; color: #215dc6; background: linear-gradient(to right, #fff 0%, #c6d3f7 100%); border-radius: 3px 3px 0 0; img { width: 16px; } }
  .pic__panel--hi { background: #d6dff7; }
  .pic__panel--hi .pic__panel__head { background: linear-gradient(to right, #0a4dc8 0%, #3c8cff 100%); color: #fff; }
  .pic__panel__body { padding: 8px 10px; }
  .pic__link { color: #215dc6; cursor: pointer; padding: 3px 0; display: flex; align-items: center; gap: 6px; line-height: 1.3;
    img { width: 16px; height: 16px; flex-shrink: 0; }
    em { font-style: normal; color: ${XP.inkDim}; margin-left: auto; }
    &:hover { color: #428eff; text-decoration: underline; }
    &--on { font-weight: 700; color: #000; } }
  .pic__details { color: ${XP.inkSoft}; line-height: 1.45; b { display: block; margin-bottom: 2px; color: #000; } }
  .pic__details__q { margin-top: 6px; color: #215dc6; font-weight: 700; }
  .pic__details__c { margin-top: 6px; font-style: italic; color: ${XP.inkDim}; }
  .pic__details__src { display: inline-block; margin-top: 6px; }

  .pic__content { flex: 1; overflow: auto; background: #fff; min-width: 0; }
  .pic__empty { padding: 26px; color: ${XP.inkSoft}; b { display: block; margin-bottom: 6px; } }
  .pic__a { color: ${XP.link}; text-decoration: underline; cursor: pointer; }
  .pic__grid { display: flex; flex-wrap: wrap; gap: 10px; padding: 12px; align-content: flex-start; }
  .pic__tile { width: 116px; cursor: default; user-select: none; border: 1px solid transparent; border-radius: 2px; padding: 4px;
    &:hover { background: #eef4ff; border-color: #98b4e6; }
    &--on { background: ${XP.selection} !important; border-color: ${XP.selection} !important; .pic__tile__name { color: #fff; } } }
  .pic__tile__frame { width: 100%; height: 92px; box-sizing: border-box; padding: 3px; background: #fff; border: 1px solid ${XP.shadow}; box-shadow: 2px 2px 3px rgba(0,0,0,.28); display: flex; align-items: center; justify-content: center; overflow: hidden;
    img { max-width: 100%; max-height: 100%; display: block; } }
  .pic__tile__name { margin-top: 5px; font-size: 10px; line-height: 1.25; color: #000; text-align: center; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .pic__filmstrip { display: flex; flex-direction: column; height: 100%; min-height: 0; }
  .pic__stage { flex: 1; min-height: 0; background: ${XP.faceAlt}; display: flex; align-items: center; justify-content: center; position: relative; padding: 12px 12px 42px;
    img { max-width: 100%; max-height: 100%; object-fit: contain; box-sizing: border-box; padding: 4px; background: #fff; border: 1px solid ${XP.shadow}; box-shadow: 3px 3px 5px rgba(0,0,0,.25); } }
  .pic__stage__bar { position: absolute; bottom: 8px; left: 0; right: 0; display: flex; align-items: center; justify-content: center; gap: 6px; color: ${XP.inkDim};
    button { min-width: 0; width: 30px; height: 22px; padding: 0; } }
  .pic__strip { height: 108px; flex-shrink: 0; display: flex; gap: 8px; overflow-x: auto; padding: 6px 10px; border-top: 1px solid ${XP.shadow}; background: #fff;
    .pic__tile { width: 92px; flex-shrink: 0; }
    .pic__tile__frame { height: 62px; } }
  .pic__table { border-collapse: collapse; width: 100%;
    th { text-align: left; font-weight: 400; color: ${XP.inkSoft}; border-bottom: 1px solid ${XP.shadow}; border-right: 1px solid ${XP.shadow}; padding: 3px 8px; position: sticky; top: 0; background: linear-gradient(180deg,#fdfdfd,#eceadd); }
    td { padding: 3px 8px; white-space: nowrap; max-width: 420px; overflow: hidden; text-overflow: ellipsis; border-bottom: 1px solid #eeede4; }
    tbody tr:hover td { background: #eef4ff; }
    tr.on td, tr.on:hover td { background: ${XP.selection}; color: #fff; }
    .c-name img { width: 20px; height: 20px; object-fit: cover; vertical-align: -5px; margin-right: 5px; border: 1px solid ${XP.shadow}; background: #fff; }
    .c-dim { color: ${XP.inkDim}; }
    tr.on .c-dim { color: #dfe8ff; } }

  .pic__preview { position: absolute; inset: 0; z-index: 4; background: ${XP.face}; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 12px; }
  .pic__preview__img { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; width: 100%; ${sunken}; padding: 8px; box-sizing: border-box;
    img { max-width: 100%; max-height: 100%; object-fit: contain; } }
  .pic__preview__bar { display: flex; gap: 6px; flex-wrap: wrap; justify-content: center; }
  .pic__preview__meta { color: ${XP.inkSoft}; text-align: center; max-width: 720px; display: flex; flex-direction: column; gap: 3px;
    b { color: #000; font-size: 13px; }
    .pic__preview__t { color: #215dc6; font-weight: 700; }
    .pic__preview__c { color: ${XP.inkDim}; line-height: 1.45; font-style: italic; }
    .pic__preview__src { color: ${XP.link}; text-decoration: underline; }
    .pic__preview__n { color: ${XP.inkDim}; font-size: 10px; } }
  .pic__about { position: absolute; inset: 0; background: rgba(0,0,0,.35); display: flex; align-items: center; justify-content: center; z-index: 5; }
  .pic__about__box { background: ${XP.face}; border: 1px solid #0a246a; box-shadow: 4px 4px 0 rgba(0,0,0,.4); padding: 14px 18px; width: 330px; color: #000; p { margin: 6px 0; } }
  .pic__about__ok { display: inline-block; margin-top: 6px; padding: 2px 18px; border: 1px solid #003c74; border-radius: 3px; background: linear-gradient(#fff,#ecebe5); }
  .pic__status { flex-shrink: 0; height: 22px; display: flex; align-items: center; gap: 3px; padding: 0 3px; border-top: 1px solid ${XP.light}; box-shadow: inset 0 1px 0 ${XP.shadow}; background: ${XP.face}; }
  .pic__status__cell { height: 17px; padding: 0 8px; display: flex; align-items: center; gap: 4px; border: 1px solid; border-color: ${XP.shadow} ${XP.light} ${XP.light} ${XP.shadow}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: ${XP.inkDim}; img { width: 14px; height: 14px; } &--grow { flex: 1; min-width: 0; } }
  @media (max-width: 800px) {
    .pic__side { display: none; }
    .pic__function_bar__text { display: none; }
    .pic__tile { width: 30%; }
    .pic__status__cell:nth-child(2), .pic__status__cell:nth-child(3) { display: none; }
  }
`;

export default Pictures;
